import {
  Alert,
  IconButton,
  InputAdornment,
  Snackbar,
  SnackbarCloseReason,
  TextField,
} from '@mui/material';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import { useCallback, useEffect } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface KeyFieldProps {
  label: string;
  keyValue: string;
}

/**
 * Generic component to display a key field with copy to clipboard and visibility toggle
 * @param props Label and key value to display
 * @returns
 */
const KeyFieldComponent = (props: KeyFieldProps) => {
  const { t } = useTranslation();
  const { label, keyValue } = props;
  const [showKey, setShowKey] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const handleCopyToClipboard = useCallback(async () => {
    navigator.clipboard.writeText(keyValue);
    setOpen(true);
  }, [keyValue]);

  const handleClickShowKey = () => setShowKey(!showKey);

  useEffect(() => {
    setShowKey(false);
  }, [keyValue]);

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <TextField
        label={t(label)}
        value={keyValue}
        variant="outlined"
        type={showKey ? 'text' : 'password'}
        sx={{ mt: 2, width: '100%' }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="copy key to clipboard"
                color="primary"
                onClick={() => handleCopyToClipboard()}
              >
                <ContentCopy />
              </IconButton>
              <IconButton
                aria-label="change key visibility"
                color="primary"
                onClick={handleClickShowKey}
              >
                {showKey ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Snackbar
        sx={{
          '& .MuiPaper-root': {
            padding: '1rem !important',
          },
        }}
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity="info">
          {t('copied_message')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default KeyFieldComponent;
